import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { TranslateModule, TranslateService } from '@itonics/ngx-translate-simple';
import { combineLatest, take, Unsubscribable } from 'rxjs';

import { KeyEventData, keyEventPathData, TimelineStep, TimelineUiComponent } from '@innovation-os-ng/ui/chart-ng';
import { ThemeService } from '@innovation-os-ng/ui/viz-tools-ng';
import { CompoundFilter, Filter, LeafFilter, RangeUtil } from '@itonics/filters';

import {
  DatePickerAlignment,
  FilterChangeAction,
  InsightsFilterOption,
  MenuContainerClassName,
  MenuContainerPlacements,
  MenuContainerTriggers,
  SignalsAggregateTimeSeries,
  SignalsFilters,
  SignalsPanel,
  SignalsType
} from '../../../shared/model/types';

import { TimeSeriesService } from '../../services/timeSeries.service';

import { categoricalColors } from '../../../shared/helpers/color-helpers';
import { getCategoryList, getDataDifferentiatedNormalized } from '../../../shared/helpers/data-processing';
import { getDatesDiff, getEndOfTheDay, getStartOfTheDay, ONE_DAY_MILLISECONDS } from '../../../shared/helpers/time-helper';

import { CommonModule, DatePipe } from '@angular/common';
import { Tenant } from '@innovation-os-ng/core/models';
import { Ui2TooltipMode, Ui2TooltipModule } from '@innovation-os-ng/ui/ui2-tooltip';
import { DefaultSignalTypes, SourcesType } from '../../../feed-widget-v2/constants/default-signal-types';
import { SearchFieldIdentifiers } from '../../../feed-widget-v2/interfaces/feed';
import { INCLUDED_TYPE_TIMELINE, MAX_NUM_RANKING } from '../../../shared/constants/constants';
import { TrackedActions, TrackedEvents } from '../../../shared/constants/tracker-ids';
import { InsightsTrackingService } from '../../../shared/modules/shared-services/services/insights-tracking-service';
import { MetricService } from '../../../shared/modules/shared-services/services/metric.service';
import { PropertiesService } from '../../../shared/modules/shared-services/services/properties.service';
import { ClusterModel } from '../../../strong-signals/model/model';
import { KeyEventPostData, NormalizedTimelinePostData } from '../../models/keyEvents.model';
import { TimelineModel, TimelineParsedResponseData } from '../../models/model';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Entity } from '@innovation-os-ng/core/shared';
import { skipper, TenantSelectors } from '@innovation-os-ng/core/state';
import { HotspotModule } from '@innovation-os-ng/ui/hotspot';
import { MenuContainerModule } from '@innovation-os-ng/ui/menu-container';
import { Ui2ToggleSwitchModule } from '@innovation-os-ng/ui/ui2-toggle-switch';
import { TitleCasePipe } from '@innovation-os-ng/ui/utils';
import { Store } from '@ngrx/store';
import * as d3 from 'd3';
import { FilterDateModule } from '../../../shared/modules/filter-date/filter-date.module';
import { ClusterScrollableListComponent } from '../../../strong-signals/components/cluster-scrollable-list/cluster-scrollable-list.component';
import { GroupByDropdownComponent } from '../group-by-dropdown/group-by-dropdown.component';
import { NormalizeSeriesComponent } from '../normalize-series/normalize-series.component';

const KEY_EVENT_PARAMS_KEY: string = 'keyEventUri';

@Component({
  selector: 'signals-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    Ui2TooltipModule,
    TranslateModule,
    MenuContainerModule,
    FilterDateModule,
    GroupByDropdownComponent,
    NormalizeSeriesComponent,
    Ui2ToggleSwitchModule,
    HotspotModule,
    ClusterScrollableListComponent,
    TimelineUiComponent,
    TitleCasePipe,
    FormsModule
  ]
})
export class TimelineComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  public documentsFilter: CompoundFilter;
  private documentsFilterChanges: Unsubscribable;

  @Input()
  public filter: CompoundFilter;

  /**
   * Receives the flag that indicates whether the switch should be visible or not.
   */
  @Input()
  public isKeyEventsFeatureFlagEnabled: boolean = false;

  /**
   * Receives the flag that indicates whether the strong signals
   * feature should be enabled or not.
   */
  @Input()
  public isStrongSignalsFeatureFlagEnabled: boolean = false;

  /**
   * Receives the flag that indicates whether show normalized data
   * as the default data for timeline.
   */
  @Input()
  public isNormalizedDataFeatureFlagEnabled: boolean = false;

  /**
   * Receives the entity.
   *
   * Needed so that we can fetch key events
   * for specific element.
   *
   * Also required to get space info.
   *
   * Current the type is any because the model in explorer-ng
   * and we cannot import else we will have circular dependency
   * issue.
   */
  @Input()
  public entity: Entity;

  /**
   * Receives the flag that indicates whether the dropdown menu is visible or not.
   */
  @Input()
  public isDropDownMenuEnabled: boolean = true;

  @Output()
  filterChanged: EventEmitter<{
    action: FilterChangeAction;
    filter: LeafFilter;
  }> = new EventEmitter();

  @Output()
  loadingChanged: EventEmitter<boolean> = new EventEmitter();

  /**
   * Notifies the parent about key event toggle state
   */
  @Output()
  public onKeyEventToggled: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Notifies the parent about cluster click
   */
  @Output()
  public onClusterCardClicked: EventEmitter<ClusterModel> = new EventEmitter<ClusterModel>();

  /**
   * Notifies the parent about selected key events
   */
  @Output()
  public keyEventSelected: EventEmitter<KeyEventData | null> = new EventEmitter<KeyEventData>();
  /*
   * DATA STATES
   */

  public noData: boolean = false;

  public isNormalizeProcessStillRunning: boolean = false;

  public isOldElement?: boolean;

  public noDataHint: boolean = false;

  /**
   * TIMELINE DATA & MODEL
   */
  private data: Array<SignalsAggregateTimeSeries>;
  private normalizedData: Array<SignalsAggregateTimeSeries>;
  public dataFiltered: Array<SignalsAggregateTimeSeries>;
  public categoryList: Array<InsightsFilterOption>;
  public dateRangeSelectModel: Array<Date>;

  public showNormalizedData: boolean;

  /**
   * Key events
   */
  // Holds the key events data used to plot in timeline.
  public keyEventsData: Array<KeyEventData> = [];
  /**
   * This initially holds the fetched key events.
   *
   * Note: Done because we initially fetch key events
   * but only assign this stored to keyEventsData when
   * we want to show the data in timeline.
   */
  public storedKeyEventsData: Array<KeyEventData> = [];
  public keyEventsDurationPathData: Record<string, Array<keyEventPathData>> = {};
  public isKeyEventsToggleEnabled: boolean = false;
  public isKeyEventsSwitchDisabled: boolean = false;
  public isKeyEventsLoading: boolean = false;
  public readonly keyEventsTooltipWidth: number = 159;
  public readonly keyEventDateFormat: string = 'YYYY-MM-dd';

  /**
   * Timeline configuration
   */
  public stepType: TimelineStep;
  public range: Array<string>;
  public showingConfig: boolean;
  public className = MenuContainerClassName.BLANK_CONTAINER;
  public trigger = MenuContainerTriggers.CLICK;
  public placement = MenuContainerPlacements.BOTTOM_START;
  public datePickerAlignment = DatePickerAlignment.LEFT;

  /**
   * Loading flag for different request.
   *
   * If there are multiple request we track every loading state.
   * So that we can hide loader when every request to fetch timeline data
   * is fetched.
   */
  private loadingFlagsState: Record<string, boolean> = {
    isNormalizedDataLoading: false,
    isAggregateDataLoading: false,
    isKeyEventsLoading: false
  };

  /**
   * Flag that indicates whether the normalized data is fetched or not.
   *
   * Note: Used to show label of data fetched in the timeline.
   */
  public isNormalizedDataFetched: boolean = false;

  /**
   * Tooltip position.
   */
  public readonly ToolTipMode: Ui2TooltipMode.INLINE = Ui2TooltipMode.INLINE;

  /**
   * Stores whether the applied source type is news or not.
   * Used for strong signals.
   */
  public isNewsType: boolean = false;

  /**
   * Stores the current tenant info
   */
  private currentTenant: Tenant;

  /**
   * Properties
   */
  // List of properties available
  public properties: Array<SignalsPanel>;
  // actual property selected in the dropdown
  public property: SignalsPanel;

  public seriesLimitMessage: string;
  private metricChangeSubs: Unsubscribable;

  // This is a hack and an input we keep alternating between to falsy values to deselct the
  // Key event in the chart everytime we need it to disappear.
  public selectedKeyEvent: KeyEventData | null | undefined = null;

  /**
   * Range when normalized data is shown in timeline.
   * We always want range to be [0,100]
   */
  public readonly normalizedDataRange: Array<number> = [0, 100];

  constructor(
    private readonly themeService: ThemeService,
    private readonly insightsTrackingService: InsightsTrackingService,
    private readonly propertiesService: PropertiesService,
    private readonly translateService: TranslateService,
    private readonly timeSeriesService: TimeSeriesService,
    private readonly metricService: MetricService,
    private readonly datePipe: DatePipe,
    private readonly route: ActivatedRoute,
    private readonly store: Store
  ) {
    // List for "GroupBy" dropdown
    this.properties = this.propertiesService.getInsightsProperties().filter((d) => INCLUDED_TYPE_TIMELINE.indexOf(d.type) !== -1);

    this.property = this.properties ? this.properties[0] : null;
  }

  ngOnInit() {
    // Fetch current tenant.
    this.fetchCurrentTenant();

    /*

      We subscribe to changes in the global metric
      to re-fetch data
    */
    this.metricChangeSubs = this.metricService.onMetricChange(async () => {
      this.fetchTimelineData();
    });
  }

  /**
   * Listen to input changes.
   *
   * @param changes - Change object.
   */
  public async ngOnChanges(changes: SimpleChanges) {
    // If key event is enabled then fetch the data.
    if (changes?.filter) {
      // Un-subs previous filter
      this.unsubscribeToFilter();

      // Assign new filter
      this.documentsFilter = this.filter;

      // Subs to new filter
      this.subscribeToFilter();

      // Fetch the required timeline data.
      this.fetchTimelineData();

      this.deselectKeyEvent();

      // Check source type
      this.isNewsType = this.isSourceTypeNews;
    }

    // Fetch normalized if FF enabled.
    if (changes?.isNormalizedDataFeatureFlagEnabled?.currentValue) {
      this.fetchNormalizedDataForTimeline();
    }
  }

  public ngOnDestroy() {
    this.unsubscribeToFilter();
    this.metricChangeSubs.unsubscribe();
  }

  /**
   * Kill the previous filter subs.
   */
  private unsubscribeToFilter(): void {
    if (this.documentsFilterChanges) {
      this.documentsFilterChanges.unsubscribe();
    }
  }

  /**
   * Subscribe to recent filter
   */
  private subscribeToFilter(): void {
    this.documentsFilterChanges = this.documentsFilter?.onChanged((changedFilter: Filter) => {
      //  If the Change comes from search but not from date picker we refresh data
      if (!(changedFilter instanceof LeafFilter && changedFilter.getOn() === SignalsType.PUBLISHED_DATE)) {
        this.fetchTimelineData();

        // Date range has changes so reset the state.
        this.resetKeyEventsState();
      }
    });
  }

  /**
   * Get the loading state of the timeline.
   */
  public get loading(): boolean {
    // Check whether every request has completed.
    return !Object.values(this.loadingFlagsState).every((flag) => !flag);
  }

  /**
   * Fetch the required timeline data.
   *
   * Note: Done so that we can fetch default
   * data for timeline based on flag.
   */
  private fetchTimelineData(): void {
    /**
     * Switch whether to show realtime or normalized data.
     */
    if (this.isNormalizedDataFeatureFlagEnabled) {
      this.fetchNormalizedDataForTimeline();
    } else {
      this.getAggregateData();
    }
  }

  public select(dateRange: Array<Date>) {
    // If the dateRange is null,
    // or if the date does not have "two" dates
    // or the date range is less than ONE day we skip selection

    if (
      !dateRange ||
      (dateRange && dateRange.length < 2) ||
      (dateRange && dateRange.length === 2 && getDatesDiff(dateRange[0], dateRange[1]) < ONE_DAY_MILLISECONDS)
    ) {
      return;
    }

    const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);

    if (dateRange && dateRange.length > 0) {
      this.trackEvent(TrackedActions.VALUE_SELECTED);

      // With this selector we get aggregated data, backend is asking for data at the end of the day
      const startDate = getStartOfTheDay(dateRange[0].getTime());
      const endDate = getEndOfTheDay(dateRange[1] ? dateRange[1].getTime() : startDate);

      filter.setRange({
        gt: { value: startDate },
        lt: { value: endDate }
      });
    } else {
      this.trackEvent(TrackedActions.VALUE_DESELECTED);
    }

    const selectionFilter = this.documentsFilter.getQuery(SignalsFilters.SELECTION);
    (selectionFilter as CompoundFilter).addFilter(filter, (oldF, newF) => {
      const oldId = oldF instanceof LeafFilter ? oldF.getOn() : oldF.getIdentifier();
      return oldId === (newF as LeafFilter).getOn();
    });
  }

  private getAggregateData() {
    if (
      !this.property ||
      !this.documentsFilter ||
      this.isNormalizedDataFeatureFlagEnabled ||
      this.loadingFlagsState.isAggregateDataLoading
    ) {
      return;
    }

    this.showLoader();

    // Set loading flag true.
    this.loadingFlagsState.isAggregateDataLoading = true;

    this.timeSeriesService.getTimelineAggregations(this.property.type, this.documentsFilter).then(
      (response) => {
        // If the normalized FF is enabled later after the request was made.
        // We want to ignore the aggregate data.
        if (this.isNormalizedDataFeatureFlagEnabled) {
          this.loadingFlagsState.isAggregateDataLoading = false;
          return;
        }
        // We filter out categories with small ranking
        // we only show categories with highest values
        this.data = response.dates.filter((d) => d.ranking <= MAX_NUM_RANKING);

        // Update the timeline data when new data fetch from backend.
        this.updateTimelineRelatedData(response);

        // Set loading flag true.
        this.loadingFlagsState.isAggregateDataLoading = false;

        // Since realtime data is fetched so we mark the flag as false.
        this.isNormalizedDataFetched = false;

        this.hideLoader();
      },
      () => {
        this.noData = true;
        this.data = [];

        // Set loading flag true.
        this.loadingFlagsState.isAggregateDataLoading = false;

        // Since realtime data is fetched so we mark the flag as false.
        this.isNormalizedDataFetched = false;

        this.hideLoader();
      }
    );
  }

  public propertyChanged(property: SignalsPanel) {
    this.property = property;
    this.insightsTrackingService.trackEvent(TrackedEvents.TIMELINE_GROUP_BY, { dimension4: property.type });

    this.fetchTimelineData();
  }

  public switchData() {
    const data = this.showNormalizedData ? this.normalizedData : this.data;

    data?.sort((a, b) => a.ranking - b.ranking);

    this.dataFiltered = !data?.length ? null : data;
  }

  public normalize(value: boolean) {
    this.insightsTrackingService.trackEvent(TrackedEvents.TIMELINE_NORMALIZE);
    this.showNormalizedData = value;
    this.switchData();
  }

  /**
   * Listens to the change event of toggle switch.
   *
   * We fetch the normalized data for timeline because we
   * have enabled the key events.
   *
   * By default key events cannot be shown to timeline realtime data.
   * We need to fetch normalized timeline data to show key events.
   *
   * @param state - State either toggled on or off.
   */
  public onKeyEventsSwitchStateChange(state: boolean): void {
    /**
     * If turned on then fetch normalized data.
     *
     * If normalized data is as default timeline data
     * then no need to fetch data again. So we set fetch flag to false.
     */
    if (state) {
      /**
       * If the normalized data is fetch by default then we
       * know that we don't need to fetch it again
       * so we directly assign to fetch key events.
       */
      if (this.isNormalizedDataFeatureFlagEnabled) {
        this.setKeyEventsData();

        // If no key event selected then select latest by default.
        if (!this.selectedKeyEvent) {
          this.selectedLatestKeyEventByDefault();
        }
      } else {
        this.fetchNormalizedDataForTimeline();
      }
    } else {
      this.deselectKeyEvent();

      // Emit null to parent
      this.keyEventSelected.emit(null);

      // Re-fetch the realtime data for timeline.
      this.getAggregateData();

      // Reset the key events state
      this.resetKeyEventsState();
    }

    this.onKeyEventToggled.emit(state);
  }

  private deselectKeyEvent() {
    // trigger the input with a new value that resolves to false to deselect!
    if (this.selectedKeyEvent === null) {
      this.selectedKeyEvent = undefined;
    } else {
      this.selectedKeyEvent = null;
    }
  }

  /**
   * Reset the key events state.
   *
   * Used when date filter is changed.
   */
  public resetKeyEventsState(): void {
    // Enable the key events button.
    this.isKeyEventsSwitchDisabled = false;

    /**
     * Turn off the key events toggle so user will
     * again turn on to fetch data for changed data range.
     */
    this.isKeyEventsToggleEnabled = false;

    /**
     * Clear the keyEvents list so that
     * key events are removed.
     */
    this.keyEventsData = [];

    /**
     * Clear selected key event
     */
    this.selectedKeyEvent = null;
  }

  /**
   * Emit cluster card click event to notify parent.
   *
   * @param cluster - Clicked cluster card detail.
   */
  public onCusterClick(cluster: ClusterModel): void {
    this.selectClusterRelatedKeyEvent(cluster.keyEventId);
    this.onClusterCardClicked.emit(cluster);
  }

  /**
   * Provides whether the signals type is
   * News or not.
   */
  public get isSourceTypeNews(): boolean {
    if (!this.filter) {
      return false;
    }

    const selectionFilter: CompoundFilter = this.filter.getQuery(SignalsFilters.SELECTION) as CompoundFilter;

    if (!selectionFilter) {
      return false;
    }

    const typeFilter: LeafFilter = selectionFilter.getQueries().find((f) => {
      if (f instanceof LeafFilter) {
        return f.getOn() === SignalsType.DOCUMENT_TYPE;
      }
    }) as LeafFilter;

    if (!typeFilter) {
      return false;
    }

    const newsType: Array<string> = typeFilter?.getValues().length ? (typeFilter.getValues() as Array<string>) : [];

    if (!newsType?.length) {
      return false;
    }

    return newsType[0] === SourcesType.NEWS;
  }

  /**
   * Select the respective key event when cluster is clicked.
   *
   * @param clusterRelatedKeyEventUri - selected cluster key event.
   */
  private selectClusterRelatedKeyEvent(clusterRelatedKeyEventUri: number): void {
    // If key event is not same then only assign.
    if (this.selectedKeyEvent?.uri !== clusterRelatedKeyEventUri) {
      this.selectedKeyEvent = this.storedKeyEventsData.find((keyEvent) => keyEvent.uri === clusterRelatedKeyEventUri) as KeyEventData;
    }
  }

  /**
   * Fetch the normalized data for timeline.
   *
   * We need to fetch this if we have key events shown
   * in timeline.
   *
   */
  private async fetchNormalizedDataForTimeline(): Promise<void> {
    /**
     * If we don't have filter we skip the request for now.
     *
     * Also, if the data is not in loading state then only fetch new.
     */
    if (!this.documentsFilter || this.loadingFlagsState.isNormalizedDataLoading) {
      return;
    }
    // Show loader.
    this.showLoader();

    // Set loading flag to true
    this.loadingFlagsState.isNormalizedDataLoading = true;

    // If tenant null fetch it first.
    if (!this.currentTenant) {
      await this.fetchCurrentTenant();
    }

    // Get the post data
    const postData: NormalizedTimelinePostData = await this.getNormalizedTimelinePostData();

    // Get the normalized timeline data.
    const parsedResponse: TimelineParsedResponseData = await this.timeSeriesService.getNormalizedTimelineData(
      postData,
      this.documentsFilter,
      this.property.type
    );

    // Update the normalized flag.
    this.isNormalizeProcessStillRunning = !parsedResponse.response.foresightProcessCompleted;

    // check if the element is older than 5 minutes ago
    this.isOldElement = this.isElementOld(this.entity.updatedOn);

    // Update the no data hint flag.
    this.noDataHint = parsedResponse.response.foresightProcessCompleted && parsedResponse.response.total === 0;

    // The timeline date data.
    this.data = parsedResponse.timelineData.dates;

    // Flag to set that normalized data was fetch in the timeline.
    this.isNormalizedDataFetched = true;

    // Set loading flag to false
    this.loadingFlagsState.isNormalizedDataLoading = false;

    /**
     * If the normalized process is not yet complete then
     * there is no point to fetch the key events.
     */
    if (!this.isNormalizeProcessStillRunning) {
      // After normalized data is fetched we show fetch key events.
      await this.fetchKeyEventsGrowthData();
    }

    /**
     * After all the normalized data and
     * key events data is fetch we update the timeline
     * with data.
     */
    this.updateTimelineRelatedData(parsedResponse.timelineData);

    // Parse the key event data after timeline data is updated.
    this.storedKeyEventsData = this.arrangeKeyEventValue(this.storedKeyEventsData);

    // If aggregate enabled recalculate the path data after aggregate calculated.
    this.prepareKeyEventDurationData(this.storedKeyEventsData);

    // Provide key event to timeline.
    this.setKeyEventsData();

    // Get the selected key event from uri.
    this.getSelectedKeyEventFromParams();

    // Hide the loader.
    this.hideLoader();
  }

  /**
   * We update the remaining data required. by timeline.
   *
   * @param response - Parsed Timeline model
   */
  private updateTimelineRelatedData(response: TimelineModel): void {
    // we limit categories to 5, we show a message if there are more
    this.setLimitMessage(this.data);

    //  Get colors for categories for this we need the labels with hierarchical data
    this.getColors(this.data, this.property.type);

    //  We also want to normalize the data to show it if the option is selected
    this.normalizedData = getDataDifferentiatedNormalized(this.data);

    // once we select in timeline, we load aggregated data
    // and we want to remove the selection afterwards
    this.dateRangeSelectModel = null;

    // Update no data flag.
    this.noData = this.data.length === 0;

    //  We show the type of data selected
    this.switchData();

    // We get the steps for the time axis and the range
    // we don't want labels as week format, so we better switch to month format
    this.stepType = response.type === TimelineStep.WEEK ? TimelineStep.MONTH : response.type;
    this.range = response.range;
  }

  /**
   * Fetch the keys events data.
   */
  private async fetchKeyEventsGrowthData(): Promise<void> {
    if (!this.entity?.uri || !this.isKeyEventsFeatureFlagEnabled) {
      return;
    }

    // Set loading flag to true.
    this.loadingFlagsState.isKeyEventsLoading = true;

    // Clear the data on every key events fetch.
    this.storedKeyEventsData = [];

    // Reset toggle state
    this.resetKeyEventsState();

    // Get the key events data.
    this.storedKeyEventsData = await this.timeSeriesService.getKeyEventsGrowthData(this.getGrowthRatePostData());

    // Check if data is present.
    const hasData: boolean = !!this.storedKeyEventsData?.length;

    // Update loading flag to false.
    this.loadingFlagsState.isKeyEventsLoading = false;

    // Transform the data after data fetched.
    this.transformKeyEventData();

    // We want to turn toggle off when key events data is empty
    this.isKeyEventsToggleEnabled = hasData;

    // We want to disable the toggle when key events data is empty.
    this.isKeyEventsSwitchDisabled = !hasData;

    // Hide loader
    this.hideLoader();
  }

  /**
   * Get previously selected key event from filter if exits.
   */
  private getSelectedKeyEventFromParams(): void {
    this.route.queryParamMap.pipe(take(1)).subscribe((params) => {
      const keyEventUri: string = params.get(KEY_EVENT_PARAMS_KEY);

      // If no uri select default the lastest.
      if (!keyEventUri) {
        this.selectedLatestKeyEventByDefault();
        return;
      }

      // Parse and find the keyevent related with given uri.
      const parseUri: number = parseInt(keyEventUri);
      const keyEvent: KeyEventData = this.storedKeyEventsData.find((k) => k?.uri === parseUri);

      // If still no key event found then select default.
      if (!keyEvent) {
        this.selectedLatestKeyEventByDefault();
        return;
      }

      // Assign the found one.
      this.selectedKeyEvent = keyEvent;
    });
  }

  /**
   * This method refactors the key event data
   * to make sure it touched the exact line above it.
   *
   * It is done so that key event plotting and the
   * average data are in sync.
   */
  private arrangeKeyEventValue(keyEventData: Array<KeyEventData>): Array<KeyEventData> {
    // Use date to bisect the data
    const bisect = d3?.bisector((d) => d.date);

    // Mapped data with updated signals count.
    const mappedKeyEventsData: Array<KeyEventData> = keyEventData?.map((d) => {
      // Make the signals count to zero
      d.signalsCount = 0;

      // Get date
      const dateString: string = this.datePipe.transform(d.executionDate, 'YYYY-MM-dd');

      // Get the left and center data for the given key event date.
      const centerIndex: number = bisect.center(this.dataFiltered, dateString);
      const closestLeftValue: SignalsAggregateTimeSeries = this.dataFiltered[centerIndex - 1];
      const closestCenter: SignalsAggregateTimeSeries = this.dataFiltered[centerIndex];

      // Date diff of key event and the nearest data left.
      const keyEventDiff: number =
        Math.abs(new Date(closestLeftValue.date).getTime() - new Date(d.executionDate).getTime()) / (1000 * 60 * 60 * 24);

      // Date diff between two two date intervals left and the center data.
      const closedDataDateDiff: number =
        Math.abs(new Date(closestLeftValue?.date).getTime() - new Date(closestCenter?.date).getTime()) / (1000 * 60 * 60 * 24);

      // Calculate the value of the key event point.
      if (!Number.isNaN(closedDataDateDiff)) {
        // Get the single day data value of 2 data points left and center.
        const singleDayData: number = Math.abs(closestLeftValue.value - closestCenter?.value) / closedDataDateDiff;
        d.signalsCount =
          closestLeftValue.value < closestCenter.value
            ? closestLeftValue.value + singleDayData * keyEventDiff
            : closestLeftValue.value - singleDayData * keyEventDiff;
      }

      return d;
    });

    return mappedKeyEventsData;
  }

  /**
   * Used to get the key event duration data
   * and used to highlight the duration of the given key event.
   *
   * What we are doing is sometime we don't
   * have exact timeline date data as the key event duration.
   * So we prepare the missing data using the data we have.
   * This way we can show the exact duration of key event
   * duration data.
   */
  private prepareKeyEventDurationData(keyEventData: Array<KeyEventData>): void {
    // Use date to bisect the data
    const bisect = d3?.bisector((d) => d.date);
    // Store all key events paths data.
    const keyEventDurationPathData: Record<string, Array<keyEventPathData>> = {};
    // Mapped data with updated signals count.
    keyEventData?.forEach((d) => {
      const pathDurationData: Array<keyEventPathData> = [];

      // Filter data according to range date range selected by user.
      const dataFiltered = this.dataFiltered.filter(
        (d) =>
          new Date(d.date).getTime() <= new Date(this.range[1]).getTime() && new Date(d.date).getTime() >= new Date(this.range[0]).getTime()
      );

      // Get date of key event.
      const startDate: string = this.datePipe.transform(d.startDate, this.keyEventDateFormat);
      const endDate: string = this.datePipe.transform(d.endDate, this.keyEventDateFormat);
      // Search in the available timeline data and get exact or close data related to key event start date.
      const startIndex: number = bisect.left(dataFiltered, startDate);
      // If nearest time greater then key event start date take one date less.
      // We don't want to miss any duration.
      const startIndexTimeStamp: number = new Date(dataFiltered[startIndex].date).getTime();
      const keyEventStartTimeStamp: number = new Date(startDate).getTime();
      let smallestEndDateValueIndex: number;
      /**
       * Get the smallest left value for key event if exact date not found.
       *
       * For example: if the key event date is 10th feb 2024 and the closet data is
       * 12th feb then we try to take less date data which will be data whose date is below 10th feb.
       */
      if (startIndexTimeStamp !== keyEventStartTimeStamp) {
        smallestEndDateValueIndex = dataFiltered[startIndex - 1] ? startIndex - 1 : startIndex;
        // Exact date not found so take one less.
        const smallClosestLeftValue: SignalsAggregateTimeSeries = dataFiltered[smallestEndDateValueIndex];
        // The value given when finding the closed which was greater than key event start date.
        const availableClosetValue: SignalsAggregateTimeSeries = dataFiltered[startIndex];
        // Date diff of key event smallest and the nearest data left.
        const difference: number =
          Math.abs(new Date(smallClosestLeftValue.date).getTime() - new Date(availableClosetValue.date).getTime()) / (1000 * 60 * 60 * 24);
        /**
         * If the difference exists then only prepare else there is no use.
         */
        if (difference !== 0) {
          const singleDayData: number = Math.abs(smallClosestLeftValue.value - availableClosetValue?.value) / difference;
          // We prepare the missing data.
          /**
           * For example: key event date is 11th feb
           * If available data is from 15th feb. Then closed will give 15th feb but we missed 4 days so we take one date less
           * which could be 5th feb. Then we prepared the data from 5-15th feb. We get the missing 4 days.
           */
          for (let i = 1; i <= difference; i++) {
            const smallClosestDate: Date = new Date(smallClosestLeftValue.date);
            const calculatedDate: Date = new Date(smallClosestDate.setDate(smallClosestDate.getDate() + i));
            // Only append data from key event start date.
            if (calculatedDate.getTime() >= keyEventStartTimeStamp) {
              const data: keyEventPathData = {
                date: this.datePipe.transform(calculatedDate, this.keyEventDateFormat),
                x: 0,
                y: 0,
                value:
                  smallClosestLeftValue.value < availableClosetValue.value
                    ? smallClosestLeftValue.value + singleDayData * i
                    : smallClosestLeftValue.value - singleDayData * i
              };
              pathDurationData.push(data);
            }
          }
        }
      }
      // End index to get key event end date nearest data.
      const endIndex: number = bisect.center(dataFiltered, endDate);
      // Slice to get the path from start to end date of the key event.
      const paths: Array<SignalsAggregateTimeSeries> = dataFiltered.slice(startIndex, endIndex);
      // All the data including the missing one.
      const allPaths: Array<SignalsAggregateTimeSeries | keyEventPathData> = [...pathDurationData, ...paths];
      // The end value should always be the key event as highlight stops at key event date.
      const mapKeyEventPathData: Array<keyEventPathData> = allPaths.map((p) => ({ date: p.date, value: p.value, x: 0, y: 0 }));
      // Add key event data as well as the final path for duration highlight
      mapKeyEventPathData.push({
        date: this.datePipe.transform(d.executionDate, this.keyEventDateFormat),
        value: d.signalsCount,
        x: 0,
        y: 0
      });
      // Keep data according to key event uri.
      keyEventDurationPathData[d.uri] = [...mapKeyEventPathData];
    });
    // Assign the all mapped duration data.
    this.keyEventsDurationPathData = keyEventDurationPathData;
  }

  /**
   * Transform the kevEventsData to show in range of 0-100
   */
  private transformKeyEventData(): void {
    combineLatest([this.timeSeriesService.cachedNormalizedTimelineDataObs, this.timeSeriesService.cachedKeyEventsGrowthDataObs])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([normalizedTimelineData, keyEventsGrowthData]) => {
        // Only transform if both maxNormalizerCount and keyEventsGrowthData are available
        if (this.isNormalizedDataFeatureFlagEnabled && !!normalizedTimelineData?.maxNormalizerCount && !!keyEventsGrowthData?.length) {
          // Evaluate the height in percentage using formula: (signalsCount/maxNormalizerCount) * 100
          this.storedKeyEventsData = keyEventsGrowthData.map((keyEventData) => {
            const updatedSignalsCount = (keyEventData.signalsCount / normalizedTimelineData.maxNormalizerCount) * 100;
            return { ...keyEventData, signalsCount: updatedSignalsCount };
          });
        }
      });
  }

  /**
   * Get the date and source type filter from the
   * filters.
   *
   * We need to post data for fetch normalized data for key event
   * and for fetching the growth key events.
   *
   * We can only filter key events with date and
   * signals types so we only get these two filters
   * from the global filters.
   *
   * @returns - Post data with updated fields.
   */
  private getGrowthRatePostData(): KeyEventPostData {
    const postData: KeyEventPostData = this.getGrowthRateDefaultPostData();

    // Get the date filter
    const dateFilter: LeafFilter = this.getDateFilter();

    // We store start date and end date.
    if (dateFilter) {
      postData.startDate = RangeUtil.convert(dateFilter?.getRange()?.gt);
      postData.endDate = dateFilter?.getRange()?.lt ? RangeUtil.convert(dateFilter?.getRange()?.lt) : Date.now();
    }

    // Get the post data with updated source types.
    const updatedPostData: KeyEventPostData = this.updatePostDataWithSourceType(postData) as KeyEventPostData;

    return updatedPostData;
  }

  /**
   * Get post data for normalized timeline data.
   *
   * @returns - Post data with updated fields.
   */
  private async getNormalizedTimelinePostData(): Promise<NormalizedTimelinePostData> {
    const postData: NormalizedTimelinePostData = await this.getNormalizedTimelineDefaultPostData();

    // Get the post data with updated source types.
    const updatedPostData: NormalizedTimelinePostData = this.updatePostDataWithSourceType(postData) as NormalizedTimelinePostData;

    return updatedPostData;
  }

  /**
   * Return the post data with updated source types.
   *
   * Note: Used to fetch growth rate and normalized data
   * for key events
   */
  private updatePostDataWithSourceType(
    postData: NormalizedTimelinePostData | KeyEventPostData
  ): NormalizedTimelinePostData | KeyEventPostData {
    // Get the source filter.
    const sourceTypeFilter: any = this.getSourceFilter();

    // Get source type
    if (sourceTypeFilter instanceof LeafFilter) {
      // Get all source types
      let sourcesType: Array<string> = DefaultSignalTypes.map((t) => t.uri);

      // Get value from filter.
      const values: Array<string> = sourceTypeFilter.getValues() as Array<string>;

      // Update the source type according to filter.
      sourcesType = sourceTypeFilter?.getExclude() ? sourcesType.filter((source) => !values.includes(source)) : values;

      // Update the post data.
      postData.docType = sourcesType;

      /**
       * If the instance is compound that means user has deselected all
       * so we update doc type with empty array.
       */
    } else if (sourceTypeFilter instanceof CompoundFilter) {
      // Update the post data.
      postData.docType = [];
    }

    // Return the post data back.
    return postData;
  }

  /**
   * Get Date filter from the global filters.
   *
   * @returns - Returns the date leaf filter if exits.
   */
  private getDateFilter(): LeafFilter | undefined {
    let dateFilter: LeafFilter;

    // Get the selection filter for start date and end date
    const selectionFilter: CompoundFilter = this.documentsFilter.getQueries().find((f) => {
      if (f instanceof CompoundFilter && (f as CompoundFilter).getIdentifier() === SearchFieldIdentifiers.SELECTION) {
        return f;
      }
    }) as CompoundFilter;

    // If selection filter present then only get the date filter.
    if (selectionFilter) {
      dateFilter = selectionFilter.getQueries().find((f) => {
        if (f instanceof LeafFilter && (f as LeafFilter).getOn() === SignalsType.PUBLISHED_DATE) {
          return f;
        }
      }) as LeafFilter;
    }

    return dateFilter;
  }

  /**
   * Get source type filter from the global filters.
   *
   * @returns - Returns the source type leaf filter if exits.
   */
  private getSourceFilter(): LeafFilter | undefined {
    // Get selection Filter.
    const selectionFilter: CompoundFilter = this.documentsFilter.getQuery(SignalsFilters.SELECTION) as CompoundFilter;

    // Get source type filter.
    const sourceTypeFilter: LeafFilter = selectionFilter.getQueries().find((query) => {
      // Get Leaf filter only
      if (query instanceof LeafFilter) {
        return (query as LeafFilter)?.getOn() === SignalsType.DOCUMENT_TYPE;

        /**
         * If instance of compound that means all the types us unchecked.
         *
         * So we return the compound filter itself.
         */
      } else if (query instanceof CompoundFilter && query.getIdentifier() === SignalsType.DOCUMENT_TYPE) {
        return true;
      }
    }) as LeafFilter;

    // Return source type.
    return sourceTypeFilter;
  }

  /**
   * Provides the default post data to fetch
   * growth rate and normalized data for timeline
   * when growth rate enabled.
   *
   * @returns - Default Post data.
   */
  private getGrowthRateDefaultPostData(): KeyEventPostData {
    /**
     * Default post data for growth Rate.
     */
    const postData: KeyEventPostData = {
      docType: null,
      elementUri: this.entity?.uri,
      spaceUri: this.entity?.spaceUri,
      startDate: null,
      endDate: null
    };

    return postData;
  }

  /**
   * Provides the default post data for fetching
   * the normalized timeline data.
   *
   * @returns - Default Post data.
   */
  private async getNormalizedTimelineDefaultPostData(): Promise<NormalizedTimelinePostData> {
    /**
     * Default post data for normalized timeline data.
     */
    const postData: NormalizedTimelinePostData = {
      docType: DefaultSignalTypes.map((t) => t.uri), // By default we pass all the types
      elementUri: this.entity?.uri,
      tenantUri: this.currentTenant?.tenantUri,
      spaceUri: this.entity?.spaceUri,
      timeRanges: []
    };

    return postData;
  }

  /**
   * Show the loader in the timeline.
   */
  private showLoader(): void {
    //  Run loading (with timestamp to track that this was the most recent request)!
    this.loadingChanged.emit(true);
  }

  /**
   * Hide the loader in the timeline.
   */
  private hideLoader(): void {
    // Only hide loader if loading is false.
    if (this.loading) {
      this.loadingChanged.emit(false);
    }
  }

  private getColors(data: Array<SignalsAggregateTimeSeries>, property: SignalsType) {
    // we register categorical colors, we need specify if is hierarchical
    categoricalColors(
      data,
      property,
      'category',
      (key, categories, predefinedMap?) => this.themeService.registerCategorical(key, categories, predefinedMap),
      // Is hierarchical? the categories are...
      property === (SignalsType.CATEGORY as string)
    );
  }

  /**
   * This function invoke when popper is showed
   */
  public popperOnShown(): void {
    this.showingConfig = true;
  }

  /**
   * This function invoke when popper is hidden
   */
  public popperOnHidden(): void {
    this.showingConfig = false;
  }

  private setLimitMessage(data: Array<SignalsAggregateTimeSeries>) {
    // If the categories that we are showing are the maximum,
    // we show the limit message
    this.seriesLimitMessage =
      getCategoryList(data).length === MAX_NUM_RANKING
        ? this.translateService.instant('signals-ng-visualization-tab.timeline.seriesLimitMessage', { value: MAX_NUM_RANKING })
        : '';
  }

  /**
   * Track the given event (after adding a few specific params)
   *
   * @param name Specific action being tracked
   */
  public trackEvent(name: string) {
    this.insightsTrackingService.trackEvent(`${TrackedEvents.PREFIX_VIZ}.${name}`, { dimension4: 'Timeline' });
  }

  public selectKeyEvent(keyEvent: KeyEventData) {
    if (this.selectedKeyEvent?.uri !== keyEvent?.uri) {
      this.selectedKeyEvent = keyEvent;
    }
    this.keyEventSelected.emit(keyEvent);
  }

  /**
   * Set keyEventsData based on normalized condition
   *
   */
  private setKeyEventsData(): void {
    this.keyEventsData = [...this.storedKeyEventsData];
  }

  /**
   * Fetch the current tenant information.
   */
  private async fetchCurrentTenant(): Promise<void> {
    return new Promise((resolve) => {
      this.store
        .select(TenantSelectors.selectCurrentTenant)
        .pipe(skipper(), takeUntilDestroyed(this.destroyRef))
        .subscribe((tenant: Tenant) => {
          this.currentTenant = tenant;
          console.assert(!!this.currentTenant, 'tenant should not be null');
          resolve();
        });
    });
  }

  /**
   * Select latest key event by default.
   */
  private selectedLatestKeyEventByDefault(): void {
    const length: number = this.storedKeyEventsData?.length;
    this.selectedKeyEvent = this.storedKeyEventsData[length - 1];
  }

  /**
   * Check if timestamp is older than 5 minutes ago
   *
   * @param {number} timestamp - The timestamp to check, in milliseconds
   * @returns {boolean} True if the timestamp is older than 5 minutes ago, false otherwise
   */
  private isElementOld(timestamp: number): boolean {
    const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
    const fiveMinutesAgo = Date.now() - FIVE_MINUTES_IN_MS;

    return timestamp < fiveMinutesAgo;
  }
}
